import useCustomSearchFilters, {
  CustomSearchState,
} from "@/hooks/useCustomSearchFilters";
import Autocomplete from "@/ui/AutoComplete";
import Container from "@/ui/Container";
import CreatableAutocomplete from "@/ui/CreatableAutoComplete";
import VenuesAutocomplete from "@/ui/VenuesAutoComplete";
import { ArrowBackIcon } from "@chakra-ui/icons";

import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Switch,
  Text,
} from "@chakra-ui/react";
import { Item, ItemTag } from "@choc-ui/chakra-autocomplete";
import { useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";

const _publicationTypes = [
  "Review",
  "JournalArticle",
  "CaseReport",
  "ClinicalTrial",
  "Dataset",
  "Editorial",
  "LettersAndComments",
  "MetaAnalysis",
  "News",
  "Study",
  "Book",
  "BookSection",
];
const CustomSearchEngine = () => {
  const { t } = useTranslation();
  const { setFormData, reset, ...rest } = useCustomSearchFilters();
  const navigate = useNavigate();
  const {
    handleSubmit,
    register,
    setValue,
    watch,
    reset: resetForm,
  } = useForm<CustomSearchState>({
    defaultValues: {
      ...rest,
    },
    resetOptions: {
      keepValues: false,
    },
  });
  const publicationVenues = watch("publicationVenues");
  const authors = watch("authors");
  const publicationTypes = watch("publicationTypes");
  const fieldsOfStudy = watch("fieldsOfStudy");
  const _fieldsOfStudy = [
    { value: "Medicine", label: t("search.filters.fields.medicine") },
    { value: "Computer Science", label: t("search.filters.fields.cs") },
    { value: "Chemistry", label: t("search.filters.fields.chemistry") },
    { value: "Biology", label: t("search.filters.fields.biology") },
    { value: "Materials Science", label: t("search.filters.fields.materials") },
    { value: "Physics", label: t("search.filters.fields.physics") },
    { value: "Geology", label: t("search.filters.fields.geology") },
    { value: "Psychology", label: t("search.filters.fields.psychology") },
    { value: "Art", label: t("search.filters.fields.art") },
    { value: "History", label: t("search.filters.fields.history") },
    { value: "Geography", label: t("search.filters.fields.geography") },
    { value: "Sociology", label: t("search.filters.fields.sociology") },
    { value: "Business", label: t("search.filters.fields.business") },
    { value: "Political Science", label: t("search.filters.fields.political") },
    { value: "Economics", label: t("search.filters.fields.economics") },
    { value: "Philosophy", label: t("search.filters.fields.philosophy") },
    { value: "Mathematics", label: t("search.filters.fields.math") },
    { value: "Engineering", label: t("search.filters.fields.engineering") },
    {
      value: "Environmental Science",
      label: t("search.filters.fields.environmental"),
    },
    {
      value: "Agricultural and Food Sciences",
      label: t("search.filters.fields.agricultural"),
    },
    { value: "Education", label: t("search.filters.fields.education") },
    { value: "Law", label: t("search.filters.fields.law") },
    { value: "Linguistics", label: t("search.filters.fields.linguistics") },
  ];
  const onSubmit = handleSubmit((data) => {
    if (
      !data.all &&
      !data.title &&
      !data.abstract &&
      data.authors.length === 0 &&
      data.publicationVenues.length === 0 &&
      data.publicationTypes.length === 0 &&
      data.fieldsOfStudy.length === 0 &&
      !data.minCitations &&
      !data.minYear &&
      !data.maxYear &&
      !data.isOpenAccess
    ) {
      return;
    }

    const query = new URLSearchParams();

    if (data.all) {
      query.set("q", data.all);
    }

    if (data.title) {
      query.set("title", data.title);
    }

    if (data.abstract) {
      query.set("abstract", data.abstract);
    }

    if (data.authors.length > 0) {
      query.append("authors", data.authors.join(","));
    }

    if (data.publicationVenues.length > 0) {
      const publicationVenues = data.publicationVenues.flatMap((venue) => {
        return venue.value;
      });
      query.append("publication_venues", publicationVenues.join(","));
    }

    if (data.publicationTypes.length > 0) {
      const publicationTypes = data.publicationTypes.flatMap((type) => {
        const _type = _publicationTypes.find(
          (e) => type.value === t(`search.filters.publicationTypes.${e}`)
        );
        if (!_type) return [];
        return _type;
      });
      query.append("publication_types", publicationTypes.join(","));
    }

    if (data.fieldsOfStudy.length > 0) {
      const fieldsOfStudy = data.fieldsOfStudy.flatMap((field) => {
        const _field = _fieldsOfStudy.find((e) => field.value === e.label);
        if (!_field) return [];
        return _field.value;
      });
      query.append("fields_of_study", fieldsOfStudy.join(","));
    }

    if (data.minCitations) {
      query.set("min_citations", data.minCitations.toString());
    }

    if (data.minYear) {
      query.set("min_year", data.minYear.toString());
    }

    if (data.maxYear) {
      query.set("max_year", data.maxYear.toString());
    }

    if (data.isOpenAccess) {
      query.set("is_open_access", data.isOpenAccess.toString());
    }
    setFormData(data);
    query.set("page", "1");
    query.set("advanced", "true");

    navigate(`/search/custom/results?${query.toString()}`);
  });

  const handleAuthorsChange = (value: Item) => {
    setValue("authors", [...authors, value.value]);
  };

  const handleAuthorsRemove = (value: ItemTag) => {
    setValue(
      "authors",
      authors.filter((author) => author !== value.label)
    );
  };

  const handlePublicationVenueChange = (value: Item) => {
    setValue("publicationVenues", [...publicationVenues, value]);
  };

  const handlePublicationVenueRemove = (value: ItemTag) => {
    setValue(
      "publicationVenues",
      publicationVenues.filter((venue) => venue.value !== value.label.value)
    );
  };

  const handlePublicationTypeChange = (value: Item) => {
    setValue("publicationTypes", [...publicationTypes, value]);
  };

  const handlePublicationTypeRemove = (value: ItemTag) => {
    setValue(
      "publicationTypes",
      publicationTypes.filter((type) => type.value !== value.label.value)
    );
  };

  const handleFieldsOfStudyChange = (value: Item) => {
    setValue("fieldsOfStudy", [...fieldsOfStudy, value]);
  };

  const handleFieldsOfStudyRemove = (value: ItemTag) => {
    setValue(
      "fieldsOfStudy",
      fieldsOfStudy.filter((field) => field.value !== value.label.value)
    );
  };

  return (
    <Container>
      <Box
        mt={8}
        p={[4, 8]}
        display={"flex"}
        flexDirection={"column"}
        alignItems={"center"}
        gap={4}
        flex={1}
        borderRadius={24}
        bgColor={"#F2F6F9"}
      >
        <Button
          alignSelf={"flex-start"}
          variant={"primary"}
          w={"fit-content"}
          onClick={() => navigate(-1)}
          mr={"auto"}
        >
          <Box
            borderWidth={1}
            borderColor={"#FFF"}
            borderRadius={100}
            w={6}
            h={6}
            display={"flex"}
            justifyContent={"center"}
            alignItems={"center"}
            mr={2}
          >
            <ArrowBackIcon />
          </Box>
          {t("article.back")}
        </Button>
        <Heading as={"h2"} textAlign={"center"} fontSize={24}>
          {t("search.advanced_search")}
        </Heading>

        <Box flexGrow={1} display={"flex"} flexDir={"column"} gap={8}>
          <Box
            as={"form"}
            onSubmit={onSubmit}
            display={"flex"}
            flexDir={"column"}
            gap={4}
          >
            {/* <FormControl>
              <FormLabel htmlFor="all">{t("search.fields.all")}</FormLabel>
              <Input
                as={Textarea}
                id="all"
                h={10}
                sx={{
                  scrollbarWidth: "none",
                }}
                borderRadius={25}
                _focusWithin={{
                  outline: "2px solid #00BCB0",
                }}
                {...register("all")}
              />
            </FormControl> */}

            <FormControl>
              <FormLabel htmlFor="title">{t("search.fields.title")}</FormLabel>
              <Input
                id="title"
                h={10}
                sx={{
                  scrollbarWidth: "none",
                }}
                _focusWithin={{
                  outline: "2px solid #00BCB0",
                }}
                {...register("title")}
              />
            </FormControl>
            {/* 
            <FormControl>
              <FormLabel htmlFor="abstract">
                {t("search.fields.abstract")}
              </FormLabel>
              <Input
                id="abstract"
                h={10}
                as={Textarea}
                sx={{
                  scrollbarWidth: "none",
                }}
                borderRadius={25}
                _focusWithin={{
                  outline: "2px solid #00BCB0",
                }}
                {...register("abstract")}
              />
            </FormControl> */}

            <FormControl>
              <FormLabel htmlFor="authors">
                {t("search.fields.authors")}
              </FormLabel>
              <CreatableAutocomplete
                id="authors"
                onChange={handleAuthorsChange}
                values={authors || []}
                options={[]}
                onTagRemove={handleAuthorsRemove}
              />
            </FormControl>
            <FormControl>
              <FormLabel htmlFor="publicationVenue">
                {t("search.fields.publicationVenue")}
              </FormLabel>
              <VenuesAutocomplete
                onChange={handlePublicationVenueChange}
                id="publicationVenue"
                values={publicationVenues || []}
                onTagRemove={handlePublicationVenueRemove}
              />
            </FormControl>
            <HStack spacing={6} flexWrap={"wrap"}>
              <FormControl flex={1} minW={300}>
                <FormLabel htmlFor="minYear">
                  {t("search.fields.minYear")}
                </FormLabel>
                <Input id="minYear" type="number" {...register("minYear")} />
              </FormControl>
              <FormControl flex={1} minW={300}>
                <FormLabel htmlFor="maxYear">
                  {t("search.fields.maxYear")}
                </FormLabel>
                <Input id="maxYear" type="number" {...register("maxYear")} />
              </FormControl>
              <FormControl flex={1} minW={300}>
                <FormLabel htmlFor="minCitations">
                  {t("search.fields.minCitations")}
                </FormLabel>
                <Input
                  id="minCitations"
                  type="number"
                  {...register("minCitations")}
                />
              </FormControl>
            </HStack>
            <HStack spacing={6} flexWrap={"wrap"}>
              <FormControl flex={1} minW={300}>
                <FormLabel htmlFor="publicationTypes">
                  {t("search.filters.publicationTypes.title")}
                </FormLabel>
                <Autocomplete
                  id="publicationTypes"
                  onChange={handlePublicationTypeChange}
                  values={publicationTypes || []}
                  options={_publicationTypes.flatMap((type) => {
                    if (
                      publicationTypes.find(
                        (_type) =>
                          _type.value ===
                          t(`search.filters.publicationTypes.${type}`)
                      )
                    ) {
                      return [];
                    }
                    return {
                      value: t(`search.filters.publicationTypes.${type}`),
                      label: t(`search.filters.publicationTypes.${type}`),
                    };
                  })}
                  onTagRemove={handlePublicationTypeRemove}
                />
              </FormControl>
              <FormControl flex={1} minW={300}>
                <FormLabel htmlFor="fieldsOfStudy">
                  {t("search.fields.fieldsOfStudy")}
                </FormLabel>
                <Autocomplete
                  id="fieldsOfStudy"
                  onChange={handleFieldsOfStudyChange}
                  values={fieldsOfStudy || []}
                  options={_fieldsOfStudy.flatMap((field) => {
                    if (fieldsOfStudy.find((f) => f.value === field.label)) {
                      return [];
                    }
                    return {
                      value: field.label,
                      label: field.label,
                    };
                  })}
                  onTagRemove={handleFieldsOfStudyRemove}
                />
              </FormControl>
            </HStack>
            <FormControl display={"flex"} alignItems={"center"} gap={4}>
              <FormLabel htmlFor="isOpenAccess" m={0}>
                {t("search.filters.isOpenAccess")}
              </FormLabel>
              <Switch
                id="isOpenAccess"
                {...register("isOpenAccess")}
                colorScheme="brand"
              />
            </FormControl>

            <Button type="submit" colorScheme={"brand"}>
              {t("search.submit")}
            </Button>
            <Button
              onClick={() => {
                resetForm();
                reset();
              }}
              variant={"outline"}
              colorScheme={"brand"}
            >
              {t("search.reset")}
            </Button>
          </Box>
          <Text
            textAlign={"center"}
            fontSize={18}
            fontStyle={"italic"}
            sx={{
              a: {
                color: "brand.500",
                textDecoration: "underline",
              },
              textWrap: "balance",
            }}
          >
            <Trans i18nKey="search.help" t={t}>
              Parcourez une base de données de +220 millions d'articles couvrant
              tous les domaines scientifiques. Pour en savoir plus, allez sur la
              rubrique
              <Link to={"/help"}>'Comment ça marche?'</Link>.
            </Trans>
          </Text>
        </Box>
      </Box>
    </Container>
  );
};

export default CustomSearchEngine;
